import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { api } from '../../lib/api';
import { useToast } from '../../components/Toast';

const EMPTY = { name: '', description: '', price: '', category_id: '', dietary_type: 'non-veg', spice_level: 0, spice_min: 0, spice_max: 0, is_available: true, is_popular: false, portion_size: '', serves: '' };

export default function MenuItems() {
  const [restaurant, setRestaurant] = useState(null);
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [togglingPopular, setTogglingPopular] = useState(null);
  const toast = useToast();

  useEffect(() => {
    api.get('/restaurants').then(data => {
      if (data.length > 0) {
        setRestaurant(data[0]);
        loadAll(data[0].restaurant_id);
      }
    }).catch(() => {});
  }, []);

  const loadAll = (rid) => {
    api.get(`/restaurants/${rid}/items`).then(setItems).catch(() => {});
    api.get(`/restaurants/${rid}/categories`).then(setCats).catch(() => {});
  };

  const openAdd = () => { setEditing(null); setForm(EMPTY); setModal(true); };
  const openEdit = (item) => {
    setEditing(item);
    setForm({ ...item, price: item.price.toString(), is_popular: item.is_popular || false });
    setModal(true);
  };
  const close = () => { setModal(false); setEditing(null); setForm(EMPTY); };
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const data = await api.upload(`/restaurants/${restaurant.restaurant_id}/upload-image`, fd);
      set('image_url', data.image_url);
      toast.success('Image uploaded!');
    } catch (err) { toast.error(err.message); } finally { setUploading(false); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!form.price || isNaN(form.price)) { toast.error('Valid price is required'); return; }
    if (!form.category_id) { toast.error('Please select a category'); return; }
    if (form.spice_min > form.spice_max && form.spice_max !== 0) { toast.error('Min spice cannot be greater than Max spice'); return; }
    setSaving(true);
    try {
      const body = { ...form, price: parseFloat(form.price), spice_level: form.spice_max };
      if (editing) {
        await api.put(`/restaurants/${restaurant.restaurant_id}/items/${editing.item_id}`, body);
        toast.success('Item updated!');
      } else {
        await api.post(`/restaurants/${restaurant.restaurant_id}/items`, body);
        toast.success('Item added!');
      }
      loadAll(restaurant.restaurant_id);
      close();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  const handleDelete = async (iid) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await api.delete(`/restaurants/${restaurant.restaurant_id}/items/${iid}`);
      toast.success('Item deleted!');
      loadAll(restaurant.restaurant_id);
    } catch (err) { toast.error(err.message); }
  };

  const toggleAvail = async (item) => {
    try {
      await api.put(`/restaurants/${restaurant.restaurant_id}/items/${item.item_id}`, { is_available: !item.is_available });
      loadAll(restaurant.restaurant_id);
    } catch (err) { toast.error(err.message); }
  };

  // ── Toggle Popular ─────────────────────────────────────────────────
  const togglePopular = async (item) => {
    setTogglingPopular(item.item_id);
    try {
      await api.put(`/restaurants/${restaurant.restaurant_id}/items/${item.item_id}/popular`, {});
      loadAll(restaurant.restaurant_id);
      const newVal = !item.is_popular;
      toast.success(newVal ? '🔥 Marked as Most Ordered!' : 'Removed from Most Ordered');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setTogglingPopular(null);
    }
  };

  const catName = (cid) => cats.find(c => c.category_id === cid)?.name || '—';

  const spiceDisplay = (item) => {
    const min = item.spice_min || 0;
    const max = item.spice_max || item.spice_level || 0;
    if (max === 0) return '—';
    if (min === 0 || min === max) return `${max}/7`;
    return `${min}–${max}`;
  };

  const filtered = items.filter(i => {
    const matchSearch = i.name.toLowerCase().includes(search.toLowerCase());
    const matchCat = filterCat === 'all' || i.category_id === filterCat;
    return matchSearch && matchCat;
  });

  const popularCount = items.filter(i => i.is_popular).length;

  return (
    <AdminLayout title="Menu Items">
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 8 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Menu Items</h2>
          <button className="btn btn-primary" onClick={openAdd}>+ Add Item</button>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <div className="search-wrapper">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35" strokeLinecap="round"/></svg>
            <input className="search-input" placeholder="Search items..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <select className="form-select" style={{width:'auto'}} value={filterCat} onChange={e => setFilterCat(e.target.value)}>
            <option value="all">All Categories</option>
            {cats.map(c => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* Popular count badge */}
          {popularCount > 0 && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: '#fff7ed', border: '1.5px solid #fed7aa',
              borderRadius: 20, padding: '5px 14px',
              fontSize: 13, fontWeight: 600, color: '#c2410c'
            }}>
              🔥 {popularCount} Most Ordered item{popularCount > 1 ? 's' : ''}
            </div>
          )}
        </div>
      </div>

      {/* Info banner */}
      {items.length > 0 && (
        <div style={{
          background: '#fff7ed', border: '1px solid #fed7aa',
          borderRadius: 10, padding: '10px 16px', marginBottom: 20,
          fontSize: 13, color: '#92400e', display: 'flex', alignItems: 'center', gap: 8
        }}>
          🔥 <span>Click the <strong>🔥</strong> button on any item to mark it as <strong>Most Ordered</strong> — it will appear first on your public menu.</span>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🍽️</div>
          <h3>{items.length === 0 ? 'No menu items yet' : 'No items match your search'}</h3>
          {items.length === 0 && <button className="btn btn-primary mt-4" onClick={openAdd}>+ Add First Item</button>}
        </div>
        ) : (
        <>
        {/* Mobile card view */}
        <div className="menu-items-cards">
          {filtered.map(item => (
            <div key={item.item_id} style={{
              background: item.is_popular ? '#fff7ed' : 'white',
              border: item.is_popular ? '1.5px solid #f97316' : '1px solid var(--border)',
              borderRadius: 12, padding: 12, display: 'flex', gap: 12
            }}>
              {/* Image */}
              <div style={{ flexShrink: 0 }}>
                {item.image_url
                  ? <img src={item.image_url} alt={item.name} style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 8 }} />
                  : <div style={{ width: 56, height: 56, background: 'var(--primary-light)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🍽️</div>
                }
              </div>
              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1a1a' }}>
                    {item.is_popular && '🔥 '}{item.name}
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--primary)', flexShrink: 0 }}>${Number(item.price).toFixed(2)}</div>
                </div>
                <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>{catName(item.category_id)}</div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
                  {item.dietary_type && item.dietary_type !== '' && (
                    <span className={`badge badge-${item.dietary_type === 'veg' ? 'veg' : item.dietary_type === 'vegan' ? 'vegan' : 'non-veg'}`}>{item.dietary_type}</span>
                  )}
                  {(item.spice_max || item.spice_level || 0) > 0 && (
                    <span style={{ fontSize: 11, color: '#b91c1c' }}>🌶️ {spiceDisplay(item)}</span>
                  )}
                  <button
                    onClick={() => toggleAvail(item)}
                    style={{
                      fontSize: 11, fontWeight: 600, padding: '2px 10px', borderRadius: 20,
                      background: item.is_available !== false ? '#16a34a' : '#ef4444',
                      color: 'white',
                      border: 'none', cursor: 'pointer'
                    }}
                  >
                    {item.is_available !== false ? '● On' : '● Off'}
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-outline btn-sm" onClick={() => openEdit(item)}>Edit</button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(item.item_id)}>Delete</button>
                  <button
                    onClick={() => togglePopular(item)}
                    style={{ background: item.is_popular ? '#fff7ed' : 'white', border: item.is_popular ? '1.5px solid #f97316' : '1.5px solid #e5e7eb', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: item.is_popular ? '#c2410c' : '#999' }}
                  >
                    🔥 {item.is_popular ? 'ON' : 'OFF'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop table view */}
        <div className="table-container menu-items-table">
          <table className="table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Name</th>
                <th>Category</th>
                <th>Price</th>
                <th>Type</th>
                <th>Spice</th>
                <th>Popular</th>
                <th>Available</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(item => (
                <tr key={item.item_id} style={{ background: item.is_popular ? '#fff7ed' : undefined }}>
                  <td>
                    {item.image_url
                      ? <img src={item.image_url} alt={item.name} style={{width:48,height:48,objectFit:'cover',borderRadius:6,border:'1px solid var(--border)'}} onError={e=>e.target.style.display='none'} />
                      : <div style={{width:48,height:48,background:'var(--primary-light)',borderRadius:6,display:'flex',alignItems:'center',justifyContent:'center',fontSize:20}}>🍽️</div>
                    }
                  </td>
                  <td>
                    <div className="font-medium" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {item.is_popular && <span style={{ fontSize: 14 }}>🔥</span>}
                      {item.name}
                    </div>
                    {item.description && <div className="text-muted text-xs truncate" style={{maxWidth:200}}>{item.description}</div>}
                  </td>
                  <td className="text-muted">{catName(item.category_id)}</td>
                  <td className="font-semibold">${Number(item.price).toFixed(2)}</td>
                  <td>
                    {item.dietary_type === 'veg-vegan' ? (
                      <><span className="badge badge-veg">veg</span> <span className="badge badge-vegan">vegan</span></>
                    ) : item.dietary_type && item.dietary_type !== '' ? (
                      <span className={`badge badge-${item.dietary_type === 'veg' ? 'veg' : item.dietary_type === 'vegan' ? 'vegan' : 'non-veg'}`}>{item.dietary_type}</span>
                    ) : null}
                  </td>
                  <td>
                    {(item.spice_max || item.spice_level || 0) > 0 ? (
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#b91c1c' }}>🌶️ {spiceDisplay(item)}</span>
                    ) : (
                      <span style={{ fontSize: 12, color: '#999' }}>—</span>
                    )}
                  </td>
                  {/* ── Popular toggle ── */}
                  <td>
                    <button
                      onClick={() => togglePopular(item)}
                      disabled={togglingPopular === item.item_id}
                      title={item.is_popular ? 'Remove from Most Ordered' : 'Mark as Most Ordered'}
                      style={{
                        background: item.is_popular ? '#fff7ed' : 'white',
                        border: item.is_popular ? '1.5px solid #f97316' : '1.5px solid #e5e7eb',
                        borderRadius: 8,
                        padding: '5px 10px',
                        cursor: togglingPopular === item.item_id ? 'not-allowed' : 'pointer',
                        fontSize: 16,
                        opacity: togglingPopular === item.item_id ? 0.6 : 1,
                        transition: 'all 0.2s',
                        display: 'flex', alignItems: 'center', gap: 4,
                      }}
                    >
                      🔥
                      <span style={{ fontSize: 11, fontWeight: 600, color: item.is_popular ? '#c2410c' : '#999' }}>
                        {item.is_popular ? 'ON' : 'OFF'}
                      </span>
                    </button>
                  </td>
                  <td>
                    <label className="toggle">
                      <input type="checkbox" checked={item.is_available} onChange={() => toggleAvail(item)} />
                      <span className="toggle-slider"></span>
                    </label>
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <button className="btn btn-outline btn-sm" onClick={() => openEdit(item)}>Edit</button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(item.item_id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
        </div>
        </>
      )}

      {modal && (
        <div className="modal-overlay" onClick={close}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{editing ? 'Edit Item' : 'Add Menu Item'}</h2>
              <button className="modal-close" onClick={close}>✕</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body space-y-4">
                <div className="form-group">
                  <label className="form-label">Item Name *</label>
                  <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Chicken Tikka Masala" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea className="form-textarea" value={form.description || ''} onChange={e => set('description', e.target.value)} placeholder="Describe the dish..." />
                </div>
                <div className="flex gap-4">
                  <div className="form-group" style={{flex:1}}>
                    <label className="form-label">Price * ($)</label>
                    <input type="number" step="0.01" min="0" className="form-input" value={form.price} onChange={e => set('price', e.target.value)} placeholder="0.00" required />
                  </div>
                  <div className="form-group" style={{flex:1}}>
                    <label className="form-label">Category *</label>
                    <select className="form-select" value={form.category_id} onChange={e => set('category_id', e.target.value)} required>
                      <option value="">Select category</option>
                      {cats.map(c => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
                    </select>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="form-group" style={{flex:1}}>
                    <label className="form-label">Dietary Type</label>
                    <select className="form-select" value={form.dietary_type} onChange={e => set('dietary_type', e.target.value)}>
                      <option value="">None</option>
                      <option value="veg">Vegetarian</option>
                      <option value="non-veg">Non-Vegetarian</option>
                      <option value="vegan">Vegan</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="form-group" style={{flex:1}}>
                    <label className="form-label">Portion Size</label>
                    <select className="form-select" value={form.portion_size || ''} onChange={e => set('portion_size', e.target.value)}>
                      <option value="">None</option>
                      <option value="8oz">8oz</option>
                      <option value="12oz">12oz</option>
                      <option value="16oz">16oz</option>
                      <option value="20oz">20oz</option>
                      <option value="32oz">32oz</option>
                    </select>
                  </div>
                  <div className="form-group" style={{flex:1}}>
                    <label className="form-label">Serves</label>
                    <select className="form-select" value={form.serves || ''} onChange={e => set('serves', e.target.value)}>
                      <option value="">None</option>
                      <option value="S1">S1 (Serves 1)</option>
                      <option value="S2">S2 (Serves 2)</option>
                      <option value="S3-4">S3-4 (Serves 3-4)</option>
                      <option value="S4-6">S4-6 (Serves 4-6)</option>
                    </select>
                  </div>
                </div>

                {/* Spice Range */}
                <div className="form-group">
                  <label className="form-label">🌶️ Spice Range (0 = not spicy)</label>
                  <div style={{ background: 'var(--primary-light)', borderRadius: 10, padding: '14px 16px' }}>
                    <div className="flex gap-4" style={{ marginBottom: 10 }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#666', display: 'block', marginBottom: 6 }}>Min (mildest it can be)</label>
                        <input type="number" min="0" max="10" className="form-input" value={form.spice_min} onChange={e => set('spice_min', parseInt(e.target.value) || 0)} placeholder="0" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#666', display: 'block', marginBottom: 6 }}>Max (hottest it can be)</label>
                        <input type="number" min="0" max="10" className="form-input" value={form.spice_max} onChange={e => set('spice_max', parseInt(e.target.value) || 0)} placeholder="0" />
                      </div>
                    </div>
                    {form.spice_max > 0 && (
                      <div>
                        <div style={{ position: 'relative', height: 6, background: '#e5e5e5', borderRadius: 10, marginBottom: 4 }}>
                          <div style={{ position: 'absolute', left: '0%', width: '100%', height: '100%', background: 'linear-gradient(to right, #fbbf24, #ef4444)', borderRadius: 10, minWidth: 6 }} />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#b91c1c' }}>
                            {form.spice_min > 0 && form.spice_min !== form.spice_max ? `🌶️ ${form.spice_min} – ${form.spice_max} spice range` : `🌶️ Level ${form.spice_max}`}
                          </span>
                        </div>
                      </div>
                    )}
                    {form.spice_max === 0 && <p style={{ fontSize: 12, color: '#999', margin: 0 }}>Set Max to 0 if this dish is not spicy</p>}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Item Image</label>
                  {form.image_url && (
                    <div className="mb-2 flex items-center gap-3">
                      <img src={form.image_url} alt="preview" className="image-preview" onError={e=>e.target.style.display='none'} />
                      <button type="button" className="btn btn-outline btn-sm" onClick={() => set('image_url', null)}>Remove</button>
                    </div>
                  )}
                  <div className="image-upload-area" onClick={() => document.getElementById('img-upload').click()}>
                    <input id="img-upload" type="file" accept="image/*" style={{display:'none'}} onChange={handleImageUpload} />
                    {uploading ? <p className="text-muted">Uploading...</p> : <p className="text-muted text-sm">📷 Click to upload image (JPEG, PNG, WebP)</p>}
                  </div>
                </div>

                {/* Most Ordered toggle in modal */}
                <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#92400e' }}>🔥 Mark as Most Ordered</div>
                    <div style={{ fontSize: 12, color: '#b45309', marginTop: 2 }}>Shows in the Most Ordered section at the top of your public menu</div>
                  </div>
                  <label className="toggle">
                    <input type="checkbox" checked={form.is_popular || false} onChange={e => set('is_popular', e.target.checked)} />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="flex items-center gap-3">
                  <label className="toggle">
                    <input type="checkbox" checked={form.is_available} onChange={e => set('is_available', e.target.checked)} />
                    <span className="toggle-slider"></span>
                  </label>
                  <span className="form-label" style={{marginBottom:0}}>Available</span>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={close}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save Item'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}